<template>
  <div class="min-h-screen flex flex-col items-center justify-center bg-black text-white p-4 sm:p-6">
    <!-- Login o Selección de Torneo/Categoría -->
    <div v-if="!juezId || !torneoId || !categoriaId" class="w-full max-w-xs sm:max-w-md">
      <div class="card !bg-white !text-gray-900 p-8">
        <h1 class="text-3xl font-bold text-center mb-2">👨‍⚖️ Acceso para Jueces</h1>
        <p class="text-center text-gray-600 mb-8">Ingresa tus credenciales</p>

        <!-- Formulario de Login -->
        <div v-if="!autenticado" class="space-y-4">
          <div>
            <label class="block text-sm font-semibold mb-2">Usuario</label>
            <input
              v-model="loginForm.usuario"
              type="text"
              placeholder="Ej: juez001"
              class="form-input"
              @keyup.enter="autenticar"
            />
          </div>

          <div>
            <label class="block text-sm font-semibold mb-2">Contraseña</label>
            <input
              v-model="loginForm.password"
              type="password"
              placeholder="••••••••"
              class="form-input"
              @keyup.enter="autenticar"
            />
          </div>

          <button
            @click="autenticar"
            :disabled="cargandoLogin"
            class="w-full btn-primary text-lg py-3"
          >
            {{ cargandoLogin ? '⏳ Autenticando...' : '✓ Iniciar Sesión' }}
          </button>
        </div>

        <!-- Selección de Torneo y Categoría -->
        <div v-else class="space-y-4">
          <div class="bg-green-100 p-3 rounded text-green-800 text-sm font-semibold">
            ✓ Bienvenido, {{ juezNombre }}
          </div>

          <div>
            <label class="block text-sm font-semibold mb-2">Torneo</label>
            <select v-model="loginForm.torneoId" class="form-select" @change="loginForm.categoriaId = ''">
              <option value="">-- Selecciona un torneo --</option>
              <option
                v-for="t in torneos"
                :key="t.id"
                :value="t.id"
              >
                {{ t.nombre }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-semibold mb-2">Categoría</label>
            <select v-model="loginForm.categoriaId" class="form-select">
              <option value="">-- Selecciona una categoría --</option>
              <option
                v-for="cat in categoriasDisponibles"
                :key="cat.categoria_id"
                :value="cat.categoria_id"
              >
                {{ cat.nombre }}
              </option>
            </select>
          </div>

          <button
            @click="iniciarSesion"
            :disabled="!loginForm.torneoId || !loginForm.categoriaId"
            class="w-full btn-primary text-lg py-3"
          >
            Continuar al Panel de Votación
          </button>

          <button
            @click="cerrarSesionLogin"
            class="w-full btn-secondary text-sm py-2"
          >
            Cambiar usuario
          </button>
        </div>

        <Alert
          v-if="alert.show"
          :type="alert.type"
          :message="alert.message"
        />
      </div>
    </div>

    <!-- Panel de Votación -->
    <div v-if="juezId && torneoId && categoriaId" class="w-full min-h-screen p-4 sm:p-6 md:p-8">
      <!-- Header -->
      <div class="bg-black bg-opacity-50 p-6 text-center mb-8">
        <p class="text-yellow-400 text-lg">Juez #{{ juezId }} | {{ nombreTorneo }}</p>
        <h2 class="text-2xl font-bold">{{ nombreCategoria }}</h2>
        <button
          @click="cerrarSesion"
          class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded text-white"
        >
          Cerrar Sesión
        </button>
      </div>

      <!-- Combate Actual -->
      <div class="max-w-4xl mx-auto">
        <Loading v-if="loadingCombate" message="Buscando combate..." />

        <div v-else-if="combateActual">
          <!-- Identificador de Llave y Categoría -->
          <div class="text-center mb-6 bg-capoeira-100 p-4 rounded-lg border-2 border-capoeira-600">
            <p class="text-capoeira-600 font-bold text-sm mb-1">{{ nombreCategoria }}</p>
            <p class="text-capoeira-700 font-bold text-2xl">{{ combateActual.llave_identificador || `Llave #${combateActual.id}` }}</p>
          </div>

          <!-- Competitors -->
          <div class="grid grid-cols-3 gap-4 mb-8">
            <!-- Blanco -->
            <div class="text-center">
              <div class="bg-yellow-600 text-black rounded-lg p-6 mb-4 border-2 border-yellow-400">
                <div class="text-6xl mb-2">⚪</div>
                <p class="font-bold text-3xl break-words text-gray-900">{{ combateActual.competidor1_apelido }}</p>
              </div>
              <!-- Botones KO/WO para Blanco -->
              <div class="flex gap-3 justify-center">
                <button
                  @click="registrarVotoEspecial('blanco', 'ko')"
                  :disabled="yaVote || votando"
                  class="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-4 rounded-lg text-lg font-bold transition-all"
                  title="Knock Out - Ganador automático"
                >
                  💥<br>KO
                </button>
                <button
                  @click="registrarVotoEspecial('blanco', 'wo')"
                  :disabled="yaVote || votando"
                  class="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-4 py-4 rounded-lg text-lg font-bold transition-all"
                  title="Walk Over - Ganador automático"
                >
                  🚶<br>WO
                </button>
              </div>
            </div>

            <!-- VS -->
            <div class="flex items-center justify-center">
              <div class="text-4xl font-bold">VS</div>
            </div>

            <!-- Negro -->
            <div class="text-center">
              <div class="bg-gray-900 text-white rounded-lg p-6 mb-4 border-2 border-yellow-500">
                <div class="text-6xl mb-2">⚫</div>
                <p class="font-bold text-3xl break-words text-yellow-400">{{ combateActual.competidor2_apelido }}</p>
              </div>
              <!-- Botones KO/WO para Negro -->
              <div class="flex gap-3 justify-center">
                <button
                  @click="registrarVotoEspecial('negro', 'ko')"
                  :disabled="yaVote || votando"
                  class="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-4 rounded-lg text-lg font-bold transition-all"
                  title="Knock Out - Ganador automático"
                >
                  💥<br>KO
                </button>
                <button
                  @click="registrarVotoEspecial('negro', 'wo')"
                  :disabled="yaVote || votando"
                  class="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-4 py-4 rounded-lg text-lg font-bold transition-all"
                  title="Walk Over - Ganador automático"
                >
                  🚶<br>WO
                </button>
              </div>
            </div>
          </div>

          <!-- Round Info -->
          <div class="text-center mb-8 bg-black bg-opacity-30 p-6 rounded-lg">
            <p class="text-2xl font-bold">
              Round {{ roundActual }} de {{ totalRounds }}
            </p>
            <p v-if="yaVote" class="text-yellow-400 text-lg mt-2">✓ Ya votaste en este round</p>
          </div>

          <!-- Botones de voto -->
          <div class="grid grid-cols-3 gap-4 mb-8">
            <!-- Blanco -->
            <button
              @click="registrarVoto('blanco')"
              :disabled="yaVote || votando"
              :class="claseBotonVoto('blanco')"
            >
              ⚪<br>BLANCO
            </button>

            <!-- Empate (solo en rounds 1 y 2) -->
            <button
              v-if="roundActual < 3"
              @click="registrarVoto('empate')"
              :disabled="yaVote || votando"
              :class="claseBotonVoto('empate')"
            >
              🤝<br>EMPATE
            </button>

            <!-- Negro -->
            <button
              @click="registrarVoto('negro')"
              :disabled="yaVote || votando"
              :class="claseBotonVoto('negro')"
            >
              ⚫<br>NEGRO
            </button>
          </div>

          <!-- Votos Recibidos en Round Actual -->
          <div class="text-center mb-6 bg-gray-800 p-4 rounded-lg">
            <p class="text-yellow-400 font-bold">Votos recibidos: {{ votosEnRoundActual }}/3</p>
            <p v-if="miVoto" class="text-green-400 font-semibold mt-1">✓ Tu voto: {{ etiquetaMiVoto }}</p>
          </div>

          <!-- Botón de Control de Combate -->
          <div class="flex justify-center mb-8">
            <!-- Pasar de Combate -->
            <button
              @click="pasarDeCombate"
              :disabled="!combateFinalizadoCompleto || votando"
              :title="!combateFinalizadoCompleto ? 'Espera a que el combate finalice' : 'Pasar al siguiente combate'"
              class="w-full max-w-sm bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-6 py-4 rounded-lg text-lg font-bold transition-all"
            >
              ⏭️ Siguiente Combate
            </button>
          </div>

          <!-- Status -->
          <div v-if="votando" class="text-center">
            <Loading message="Registrando voto..." />
          </div>

          <div v-else-if="mensajeVoto" :class="clasesMensaje" class="text-center p-6 rounded-lg text-lg font-bold">
            {{ mensajeVoto }}
          </div>
        </div>

        <div v-else class="text-center py-12 text-xl">
          ⏳ Esperando primer combate...
        </div>
      </div>

      <Alert
        v-if="alert.show"
        :type="alert.type"
        :message="alert.message"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAPI } from '../../composables/useAPI'
import Loading from '../../components/Loading.vue'
import Alert from '../../components/Alert.vue'

const route = useRoute()
const api = useAPI()

// State
const juezId = ref(null)
const juezNombre = ref('')
const torneoId = ref(null)
const categoriaId = ref(null)
const torneos = ref([])
const combateActual = ref(null)
const loadingCombate = ref(false)
const votando = ref(false)
const yaVote = ref(false)
const mensajeVoto = ref('')
const refreshInterval = ref(null)
const autenticado = ref(false)
const cargandoLogin = ref(false)

const loginForm = ref({
  usuario: '',
  password: '',
  torneoId: '',
  categoriaId: ''
})

const alert = ref({
  show: false,
  type: 'info',
  message: ''
})

const categoriasDisponibles = computed(() => {
  const torneo = torneos.value.find(t => t.id === parseInt(loginForm.value.torneoId))
  return torneo?.categorias || []
})

const nombreTorneo = computed(() => {
  const torneo = torneos.value.find(t => t.id === torneoId.value)
  return torneo?.nombre || ''
})

const nombreCategoria = computed(() => {
  if (!combateActual.value) return ''
  return combateActual.value.categoria_nombre || ''
})

const roundActual = computed(() => {
  if (!combateActual.value) return 1
  // Buscar primer round pendiente o en curso
  const rounds = combateActual.value.rounds || []
  const proximoRound = rounds.find(r => r.estado === 'pendiente' || r.estado === 'en_curso')
  return proximoRound?.numero_round || 1
})

const totalRounds = computed(() => {
  if (!combateActual.value) return 2
  return (combateActual.value.rounds || []).length
})

const votosEnRoundActual = computed(() => {
  if (!combateActual.value) return 0
  const roundInfo = combateActual.value.rounds?.[roundActual.value - 1]
  return roundInfo?.votos?.length || 0
})

// Voto que este juez emitió en el round actual (blanco/negro/empate), o null si aún no vota
const miVoto = computed(() => {
  if (!combateActual.value) return null
  const roundInfo = combateActual.value.rounds?.[roundActual.value - 1]
  const voto = roundInfo?.votos?.find(v => v.juez_id === juezId.value)
  return voto?.voto || null
})

const etiquetaMiVoto = computed(() => {
  const etiquetas = { blanco: 'BLANCO', negro: 'NEGRO', empate: 'EMPATE' }
  return etiquetas[miVoto.value] || miVoto.value
})

// Clases del botón de voto: color normal si aún no ha votado,
// gris apagado/deshabilitado para todos una vez que ya votó
const claseBotonVoto = (color) => {
  if (yaVote.value) {
    return 'btn-large bg-gray-400 text-gray-500 border-4 border-gray-400 opacity-60 cursor-not-allowed'
  }

  return {
    blanco: 'btn-white-large',
    negro: 'btn-black-large',
    empate: 'btn-tie-large'
  }[color]
}

const combateFinalizadoCompleto = computed(() => {
  if (!combateActual.value) return false
  return combateActual.value.estado === 'finalizado'
})

const clasesMensaje = computed(() => {
  if (mensajeVoto.value.includes('exitosamente')) return 'bg-green-600'
  if (mensajeVoto.value.includes('error')) return 'bg-red-600'
  return 'bg-blue-600'
})

// Guardar selecciones en localStorage
watch(() => loginForm.value.torneoId, (newVal) => {
  if (newVal) {
    localStorage.setItem('juez_torneoId', newVal)
  }
})

watch(() => loginForm.value.categoriaId, (newVal) => {
  if (newVal) {
    localStorage.setItem('juez_categoriaId', newVal)
  }
})

const autenticar = async () => {
  if (!loginForm.value.usuario || !loginForm.value.password) {
    showAlert('error', 'Por favor ingresa usuario y contraseña')
    return
  }

  try {
    cargandoLogin.value = true
    const response = await api.apiCall('POST', '/jueces/login', {
      usuario: loginForm.value.usuario,
      password: loginForm.value.password
    })

    juezId.value = response.id
    juezNombre.value = response.nombre
    autenticado.value = true

    // Guardar sesión en localStorage
    localStorage.setItem('juezSesion', JSON.stringify({
      juezId: response.id,
      juezNombre: response.nombre,
      usuario: loginForm.value.usuario,
      timestamp: Date.now()
    }))

    showAlert('success', '✓ Autenticación exitosa')
  } catch (err) {
    showAlert('error', err.message)
  } finally {
    cargandoLogin.value = false
  }
}

const cerrarSesionLogin = () => {
  autenticado.value = false
  juezId.value = null
  juezNombre.value = ''
  loginForm.value = {
    usuario: '',
    password: '',
    torneoId: '',
    categoriaId: ''
  }
}

const iniciarSesion = async () => {
  if (!loginForm.value.torneoId || !loginForm.value.categoriaId) {
    showAlert('error', 'Por favor selecciona torneo y categoría')
    return
  }

  torneoId.value = parseInt(loginForm.value.torneoId)
  categoriaId.value = parseInt(loginForm.value.categoriaId)

  console.log('Iniciando sesión con Torneo:', torneoId.value, 'Categoría:', categoriaId.value)

  await buscarCombate()

  // Actualizar datos cada 5 segundos vía polling (sin SSE)
  if (refreshInterval.value) clearInterval(refreshInterval.value)
  refreshInterval.value = setInterval(actualizarCombatePolling, 5000)
}

const actualizarCombatePolling = async () => {
  if (!torneoId.value || !categoriaId.value) return
  // Reutiliza buscarCombate (sin loading) para que también avance automáticamente
  // al siguiente combate cuando los 3 jueces ya confirmaron, aunque este juez no haga click.
  await buscarCombate(false)
}

const cerrarSesion = () => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
    refreshInterval.value = null
  }
  juezId.value = null
  juezNombre.value = ''
  torneoId.value = null
  categoriaId.value = null
  combateActual.value = null
  mensajeVoto.value = ''
  yaVote.value = false
  autenticado.value = false
  loginForm.value = { usuario: '', password: '', torneoId: '', categoriaId: '' }

  // Limpiar sesión del localStorage
  localStorage.removeItem('juezSesion')
}

const buscarCombate = async (mostrarLoading = true) => {
  try {
    if (mostrarLoading) loadingCombate.value = true

    // Validar que hay categoría seleccionada
    if (!categoriaId.value) {
      showAlert('error', 'No hay categoría seleccionada')
      return
    }

    const bracket = await api.getBracket(torneoId.value, categoriaId.value)
    let avanzarSiguiente = false

    // Si hay un combate actual finalizado, verificar si todos los jueces confirmaron
    if (combateActual.value && combateActual.value.estado === 'finalizado') {
      try {
        const confirmaciones = await api.verificarConfirmaciones(combateActual.value.id)

        // Si no hay 3 confirmaciones aún, mantener el combate actual visible
        if (!confirmaciones.todos_confirmaron) {
          const combateActualizado = bracket.rondas
            .flatMap(r => r.llaves)
            .find(l => l.id === combateActual.value.id)

          if (combateActualizado) {
            combateActual.value = combateActualizado
          }

          mensajeVoto.value = `⏳ Esperando confirmación de otros jueces (${confirmaciones.confirmaciones}/3)`
          return
        }

        // Todos los jueces confirmaron: pasar automáticamente al siguiente combate
        avanzarSiguiente = true
      } catch (err) {
        console.error('Error verificando confirmaciones:', err)
      }
    }

    // Si hay un combate actual (y no toca avanzar), recargarlo con datos frescos
    if (combateActual.value && !avanzarSiguiente) {
      const combateActualizado = bracket.rondas
        .flatMap(r => r.llaves)
        .find(l => l.id === combateActual.value.id)

      if (combateActualizado) {
        combateActual.value = combateActualizado
        mensajeVoto.value = ''
        verificarVoto()
        return
      }
    }

    // Buscar el primer combate no finalizado EN ESTA CATEGORÍA (siguiente combate)
    for (const ronda of bracket.rondas) {
      const combate = ronda.llaves.find(l => l.estado !== 'finalizado' && !l.es_bye && l.categoria_id === categoriaId.value)
      if (combate) {
        const esNuevo = !combateActual.value || combateActual.value.id !== combate.id
        combateActual.value = combate
        verificarVoto()

        if (avanzarSiguiente && esNuevo) {
          mensajeVoto.value = '✓ Nuevo combate cargado'
          setTimeout(() => { mensajeVoto.value = '' }, 2000)
        } else {
          mensajeVoto.value = ''
        }
        return
      }
    }

    // No hay combates pendientes
    combateActual.value = null
    mensajeVoto.value = '✓ Tornéo completado para esta categoría'
  } catch (err) {
    showAlert('error', err.message)
  } finally {
    if (mostrarLoading) loadingCombate.value = false
  }
}

const verificarVoto = () => {
  if (!combateActual.value || !combateActual.value.rounds) {
    yaVote.value = false
    return
  }

  const roundInfo = combateActual.value.rounds[roundActual.value - 1]
  const votos = roundInfo?.votos || []
  yaVote.value = votos.some(v => v.juez_id === juezId.value)
}

const registrarVoto = async (voto) => {
  try {
    votando.value = true
    mensajeVoto.value = ''

    const response = await api.registrarVoto(
      combateActual.value.id,
      roundActual.value,
      juezId.value,
      voto
    )

    mensajeVoto.value = `✓ Voto registrado (${response.votos_recibidos}/3)`
    yaVote.value = true

    // Actualizar localmente de inmediato para que la etiqueta "Tu voto" aparezca al instante,
    // sin esperar al próximo refresco (polling cada 5s)
    registrarVotoLocal(voto)

    // Limpiar mensaje después de 2 segundos
    setTimeout(() => {
      mensajeVoto.value = ''
    }, 2000)
  } catch (err) {
    mensajeVoto.value = `✕ Error: ${err.message}`
    showAlert('error', err.message)
  } finally {
    votando.value = false
  }
}

// Refleja el voto recién emitido en combateActual localmente (optimista),
// para no depender del próximo polling
const registrarVotoLocal = (voto) => {
  const roundInfo = combateActual.value?.rounds?.[roundActual.value - 1]
  if (!roundInfo) return

  if (!roundInfo.votos) roundInfo.votos = []

  const yaExiste = roundInfo.votos.some(v => v.juez_id === juezId.value)
  if (!yaExiste) {
    roundInfo.votos.push({ juez_id: juezId.value, voto })
  }
}

const registrarVotoEspecial = async (color, tipo) => {
  try {
    votando.value = true
    mensajeVoto.value = ''

    // Registrar voto con tipo KO o WO
    const response = await api.registrarVoto(
      combateActual.value.id,
      roundActual.value,
      juezId.value,
      color,
      tipo // tipo: 'ko' o 'wo'
    )

    const tipoNombre = tipo === 'ko' ? 'KO' : 'WO'
    mensajeVoto.value = `✓ ${color.toUpperCase()} - ${tipoNombre} (Ganador automático)`
    yaVote.value = true

    // Reflejar el voto localmente de inmediato (misma lógica que el voto normal)
    registrarVotoLocal(color)

    // Refrescar después de 2 segundos
    setTimeout(() => {
      buscarCombate()
      mensajeVoto.value = ''
    }, 2000)
  } catch (err) {
    mensajeVoto.value = `✕ Error: ${err.message}`
    showAlert('error', err.message)
  } finally {
    votando.value = false
  }
}

const pasarDeCombate = async () => {
  if (!combateFinalizadoCompleto.value) {
    showAlert('warning', 'El combate aún no ha finalizado')
    return
  }

  try {
    // Registrar confirmación de este juez (idempotente: si ya confirmó, no falla)
    mensajeVoto.value = '⏭️ Registrando confirmación...'
    await api.registrarConfirmacionJuez(combateActual.value.id, juezId.value)

    // buscarCombate se encarga de avanzar automáticamente si los 3 jueces ya confirmaron,
    // o de mostrar el mensaje de espera si aún faltan confirmaciones
    await buscarCombate()
  } catch (err) {
    mensajeVoto.value = `✕ Error: ${err.message}`
    showAlert('error', err.message)
  }
}

const showAlert = (type, message) => {
  alert.value = { show: true, type, message }
}

onMounted(async () => {
  try {
    // Restaurar sesión del localStorage si existe
    const sesionGuardada = localStorage.getItem('juezSesion')
    if (sesionGuardada) {
      const sesion = JSON.parse(sesionGuardada)
      // Verificar que la sesión no sea más antigua de 24 horas
      if (Date.now() - sesion.timestamp < 86400000) {
        juezId.value = sesion.juezId
        juezNombre.value = sesion.juezNombre
        autenticado.value = true
        console.log('Sesión restaurada para juez:', sesion.juezNombre)
      } else {
        localStorage.removeItem('juezSesion')
      }
    }

    torneos.value = await api.getTorneos()

    // Restaurar selecciones de torneo y categoría
    const torneoGuardado = localStorage.getItem('juez_torneoId')
    const categoriaGuardada = localStorage.getItem('juez_categoriaId')

    if (torneoGuardado && categoriaGuardada && autenticado.value) {
      loginForm.value.torneoId = parseInt(torneoGuardado)
      loginForm.value.categoriaId = parseInt(categoriaGuardada)
      // Iniciar sesión automáticamente con las selecciones guardadas
      await iniciarSesion()
    }
  } catch (err) {
    showAlert('error', err.message)
  }
})

onUnmounted(() => {
  if (refreshInterval.value) clearInterval(refreshInterval.value)
})
</script>
